import classNames from 'classnames'
import { useState } from 'react'

import { t } from '../../../common/i18n.js'
import { Language } from '../../../common/types/i18n.js'
import { PlaytimeQuestion, ScaleLabels } from '../../../common/types/playtime.js'
import { questionText, questionTooltip } from '../../../common/utils/q12-question-text.js'
import { Tooltip } from '../tooltip/tooltip.js'
import './survey-question.scss'

export interface SurveyQuestionProps {
    question: PlaytimeQuestion
    value: string
    onChange: (value: string) => void
    lang: Language
    scaleLabels?: ScaleLabels | null
}

const getDefaultLikertLabels = (lang: Language) => [
    t.playtime.q12.likert.stronglyDisagree(lang),
    t.playtime.q12.likert.disagree(lang),
    t.playtime.q12.likert.neutral(lang),
    t.playtime.q12.likert.agree(lang),
    t.playtime.q12.likert.stronglyAgree(lang),
]

const getLikertLabels = (lang: Language, scaleLabels?: ScaleLabels | null): string[] => {
    if (!scaleLabels) {
        return getDefaultLikertLabels(lang)
    }

    return ['1', '2', '3', '4', '5'].map((key, index) => {
        const custom = scaleLabels[key]
        return custom?.[lang] || getDefaultLikertLabels(lang)[index]
    })
}

const getNoResponseLabel = (lang: Language, scaleLabels?: ScaleLabels | null): string => {
    const custom = scaleLabels?.['0']
    return custom?.[lang] || t.playtime.q12.noResponse(lang)
}

/**
 * Optional clarification of a question (`questions.description`), shown as an
 * info icon with a hover tooltip next to the question text.
 */
const QuestionTooltip = ({ text }: { text: string }) => {
    const [isShown, setIsShown] = useState(false)
    return (
        <Tooltip
            element="span"
            triggerEvent="hover"
            placement="top"
            triggerClassName="survey-question__tooltip"
            isShown={isShown}
            onToggle={setIsShown}
        >
            {text}
        </Tooltip>
    )
}

const QuestionTitle = ({ question, lang }: { question: PlaytimeQuestion; lang: Language }) => {
    const tooltip = questionTooltip(question, lang)
    return (
        <legend className="survey-question__title">
            {questionText(question, lang)}
            {question.is_required && <span className="survey-question__required-asterisk">*</span>}
            {tooltip && <QuestionTooltip text={tooltip} />}
        </legend>
    )
}

export const SurveyQuestion = ({
    question,
    value,
    onChange,
    lang,
    scaleLabels,
}: SurveyQuestionProps) => {
    const isLikert = question.type === 'likert' || question.type === 'range'
    const isText = question.type === 'text' || question.type === 'textarea'

    if (isLikert) {
        const labels = getLikertLabels(lang, scaleLabels)
        return (
            <fieldset className="survey-question">
                <QuestionTitle question={question} lang={lang} />
                <div className="survey-question__options">
                    {labels.map((label, index) => {
                        const optionValue = String(index + 1)
                        return (
                            <label
                                key={optionValue}
                                className={classNames(
                                    'survey-question__option',
                                    value === optionValue && 'survey-question__option--selected',
                                )}
                            >
                                <input
                                    type="radio"
                                    name={`question-${question.id}`}
                                    value={optionValue}
                                    checked={value === optionValue}
                                    onChange={() => onChange(optionValue)}
                                    className="survey-question__radio"
                                />
                                <span className="survey-question__option-indicator">
                                    {optionValue}
                                </span>
                                <span className="survey-question__option-label">{label}</span>
                            </label>
                        )
                    })}
                    {/* No response option */}
                    <label
                        className={classNames(
                            'survey-question__option',
                            'survey-question__option--no-response',
                            value === '0' && 'survey-question__option--selected',
                        )}
                    >
                        <input
                            type="radio"
                            name={`question-${question.id}`}
                            value="0"
                            checked={value === '0'}
                            onChange={() => onChange('0')}
                            className="survey-question__radio"
                        />
                        <span className="survey-question__option-indicator">—</span>
                        <span className="survey-question__option-label">
                            {getNoResponseLabel(lang, scaleLabels)}
                        </span>
                    </label>
                </div>
            </fieldset>
        )
    }

    if (isText) {
        return (
            <fieldset className="survey-question">
                <QuestionTitle question={question} lang={lang} />
                <textarea
                    className="survey-question__textarea"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    rows={4}
                    placeholder={t.playtime.q12.yourAnswer(lang)}
                />
            </fieldset>
        )
    }

    // Default: text input
    return (
        <fieldset className="survey-question">
            <QuestionTitle question={question} lang={lang} />
            <input
                type="text"
                className="survey-question__input"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={t.playtime.q12.yourAnswer(lang)}
            />
        </fieldset>
    )
}
